import React, { useState, useEffect } from "react";
import { database } from "../firebaseConfig";
import { ref, update, onValue } from "firebase/database";
import "./SafeGuardSettings.css";

const SafeGuardSettings = ({ userId }) => {
  const [safeGuardSettings, setSafeGuardSettings] = useState({
    enabled: false, // NEU: SafeGuard standardmäßig deaktiviert
    emailDos: "",
    emailDonts: "",
    targetAudience: "",
    veryImportant: "",
    minimumRating: 7 // Standard: 7 von 10
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isSaved, setIsSaved] = useState(false);

  // Lade vorhandene Einstellungen aus der Datenbank
  useEffect(() => {
    if (!userId) return;
    
    const safeGuardSettingsRef = ref(database, `/World/${userId}/settings/safeGuardSettings`);
    const unsubscribeListener = onValue(
      safeGuardSettingsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          setSafeGuardSettings({
            enabled: data.enabled !== undefined ? data.enabled : false, // Standardmäßig deaktiviert
            emailDos: data.emailDos || "",
            emailDonts: data.emailDonts || "",
            targetAudience: data.targetAudience || "",
            veryImportant: data.veryImportant || "",
            minimumRating: data.minimumRating || 7
          });
        }
      },
      (error) => {
        console.error("Fehler beim Laden der SafeGuard Settings", error);
      }
    );

    return () => unsubscribeListener();
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSafeGuardSettings(prev => ({
      ...prev,
      [name]: value
    }));
    setIsSaved(false);
  };

  const handleSave = async () => {
    if (!userId) {
      setMessage("User nicht gefunden.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const safeGuardSettingsRef = ref(database, `/World/${userId}/settings/safeGuardSettings`);
      await update(safeGuardSettingsRef, {
        enabled: safeGuardSettings.enabled, // NEU: SafeGuard Aktivierungsstatus speichern
        emailDos: safeGuardSettings.emailDos.trim(),
        emailDonts: safeGuardSettings.emailDonts.trim(),
        targetAudience: safeGuardSettings.targetAudience.trim(),
        veryImportant: safeGuardSettings.veryImportant.trim(),
        minimumRating: safeGuardSettings.minimumRating
      });
      
      setMessage("SafeGuard Settings successfully saved! The AI SafeGuard will now check your emails before sending.");
      setIsSaved(true);
      
      setTimeout(() => {
        setMessage("");
        setIsSaved(false);
      }, 3000);
    } catch (error) {
      console.error("Fehler beim Speichern der SafeGuard Settings:", error);
      setMessage("Fehler beim Speichern der SafeGuard Settings: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="safeGuardSettingsContainer">
      <div className="settingsSection">
        <h3 className="sectionTitle">Email SafeGuard Settings</h3>
        <p className="sectionDescription">
          Configure your AI SafeGuard to automatically review all outgoing emails before sending. 
          The AI will check your emails against these guidelines and either approve them or flag them for review.
        </p>

        {/* SafeGuard Toggle Switch */}
        <div className="settingGroup safeGuardToggleGroup">
          <div className="toggleContainer">
            <label className="toggleSwitch">
              <input
                type="checkbox"
                name="enabled"
                checked={safeGuardSettings.enabled}
                onChange={(e) => setSafeGuardSettings(prev => ({ ...prev, enabled: e.target.checked }))}
              />
              <span className="toggleSlider"></span>
            </label>
            <div className="toggleLabelContainer">
              <div className="toggleLabel">
                {safeGuardSettings.enabled ? '🛡️ SafeGuard Active' : '🔓 SafeGuard Disabled'}
              </div>
              <div className="toggleDescription">
                {safeGuardSettings.enabled 
                  ? 'All emails will be checked by AI before sending'
                  : 'Emails will be sent immediately without AI review'
                }
              </div>
            </div>
          </div>
        </div>
        
        <div className="settingGroup">
          <label className="settingLabel">
            Email DOs ✅
            <span className="settingDescription">
              Define what your emails SHOULD include, contain, or follow. Be specific about tone, content, and style preferences.
            </span>
          </label>
          <textarea
            name="emailDos"
            value={safeGuardSettings.emailDos}
            onChange={handleChange}
            className="settingTextarea"
            placeholder="Example:
- Always use a professional but friendly tone
- Include specific value propositions
- Keep emails under 150 words
- Always end with a clear call-to-action
- Use personalized greetings
- Focus on customer benefits..."
            rows="8"
          />
        </div>

        <div className="settingGroup">
          <label className="settingLabel">
            Email DON'Ts ❌
            <span className="settingDescription">
              Define what your emails should NEVER include or do. List forbidden words, phrases, or approaches.
            </span>
          </label>
          <textarea
            name="emailDonts"
            value={safeGuardSettings.emailDonts}
            onChange={handleChange}
            className="settingTextarea"
            placeholder="Example:
- Never use aggressive sales language
- Don't mention competitors by name
- Avoid using ALL CAPS
- No spam-like phrases ('URGENT', 'LIMITED TIME')
- Don't make unrealistic promises
- Avoid technical jargon without explanation..."
            rows="8"
          />
        </div>

        <div className="settingGroup">
          <label className="settingLabel">
            Target Audience 🎯
            <span className="settingDescription">
              Describe your ideal customer/audience to help the AI understand the appropriate communication style.
            </span>
          </label>
          <textarea
            name="targetAudience"
            value={safeGuardSettings.targetAudience}
            onChange={handleChange}
            className="settingTextarea"
            placeholder="Example:
- Small to medium business owners aged 30-50
- Decision makers looking for efficiency solutions
- Budget-conscious but value-oriented
- Prefer direct, honest communication
- Busy professionals who appreciate concise messages
- Tech-savvy but not developers..."
            rows="6"
          />
        </div>

        <div className="settingGroup">
          <label className="settingLabel">
            Very Important Notes ⚠️
            <span className="settingDescription">
              Critical guidelines that must ALWAYS be followed. These are your non-negotiable rules.
            </span>
          </label>
          <textarea
            name="veryImportant"
            value={safeGuardSettings.veryImportant}
            onChange={handleChange}
            className="settingTextarea"
            placeholder="Example:
- NEVER promise specific ROI percentages
- Always comply with GDPR regulations
- Must include unsubscribe option in marketing emails
- Never send emails outside business hours (9 AM - 6 PM)
- Always double-check recipient names for accuracy
- Critical: Never mention internal company issues..."
            rows="6"
          />
        </div>

        <div className="settingGroup">
          <label className="settingLabel">
            Minimum Rating Threshold 🎯
            <span className="settingDescription">
              Set the minimum SafeGuard rating (1-10) required for automatic email sending. 
              Emails with lower ratings will be saved as drafts for manual review.
            </span>
          </label>
          
          <div className="ratingSliderContainer">
            <div className="ratingSliderLabels">
              <span className="ratingLabel strict">Strict (1-3)</span>
              <span className="ratingLabel moderate">Moderate (4-7)</span>
              <span className="ratingLabel lenient">Lenient (8-10)</span>
            </div>
            
            <div className="ratingSliderWrapper">
              <input
                type="range"
                name="minimumRating"
                min="1"
                max="10"
                value={safeGuardSettings.minimumRating}
                onChange={handleChange}
                className="ratingSlider"
              />
              <div className="ratingSliderTicks">
                {[1,2,3,4,5,6,7,8,9,10].map(num => (
                  <div key={num} className="ratingSliderTick">{num}</div>
                ))}
              </div>
            </div>
            
            <div className="ratingCurrentValue">
              <span className="ratingValueLabel">Current Threshold:</span>
              <span className={`ratingValue ${
                safeGuardSettings.minimumRating <= 3 ? 'strict' : 
                safeGuardSettings.minimumRating <= 7 ? 'moderate' : 'lenient'
              }`}>
                {safeGuardSettings.minimumRating}/10
              </span>
            </div>
            
            <div className="ratingExplanation">
              {safeGuardSettings.minimumRating <= 3 && (
                <div className="ratingExplanationText strict">
                  <strong>Very Strict:</strong> Only emails with excellent ratings will be sent automatically. 
                  Most emails will require manual review.
                </div>
              )}
              {safeGuardSettings.minimumRating > 3 && safeGuardSettings.minimumRating <= 7 && (
                <div className="ratingExplanationText moderate">
                  <strong>Balanced:</strong> Good quality emails will be sent automatically. 
                  Poor quality emails will be flagged for review.
                </div>
              )}
              {safeGuardSettings.minimumRating > 7 && (
                <div className="ratingExplanationText lenient">
                  <strong>Lenient:</strong> Most emails will be sent automatically. 
                  Only very poor emails will be flagged for review.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="safeGuardInfo">
          <div className="infoIcon">🛡️</div>
          <div className="infoContent">
            <div className="infoTitle">How SafeGuard Works</div>
            <div className="infoDescription">
              Before any email is sent or scheduled, our AI SafeGuard will:
              <ul>
                <li>✅ Check against your DOs and DON'Ts</li>
                <li>🎯 Verify appropriateness for your target audience</li>
                <li>⚠️ Ensure compliance with your critical rules</li>
                <li>📝 Either approve for sending or flag for manual review</li>
              </ul>
              Flagged emails will be saved as drafts for your review instead of being sent.
            </div>
          </div>
        </div>
      </div>

      <div className="settingActions">
        <button 
          className={`saveSettingsButton ${isSaved ? 'saved' : ''}`} 
          onClick={handleSave}
          disabled={loading}
          style={{
            backgroundColor: isSaved ? '#10b981' : 'var(--blue)',
            transition: 'background-color 0.3s ease'
          }}
        >
          {loading ? 'Saving...' : isSaved ? 'Saved!' : 'Save SafeGuard Settings'}
        </button>
      </div>

      {message && (
        <p className={`world_settings_message ${message.includes('erfolgreich') || message.includes('successfully') ? 'success' : 'error'}`}>
          {message}
        </p>
      )}
    </div>
  );
};

export default SafeGuardSettings; 